import { DataTable } from 'components/DataTable/DataTable';
import { ControlledTextField } from 'components/Form';
import type { ICollapseFieldsToCheckItem } from 'components/Form/ControlledCollapseGroup';
import { ControlledCollapseGroup } from 'components/Form/ControlledCollapseGroup';
import { Card, GridItem, cnMixSpace } from 'uikit';
import { validateInterval } from 'utils';
import { requiredBaseRule } from 'utils/constants';

import type React from 'react';
import { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import type { TBimContractConversionIndexInput } from '../../hooks/useBimConversionIndicesColumns';
import { useBimConversionIndicesColumns } from '../../hooks/useBimConversionIndicesColumns';
import type { TBimContractSeasonInflationInputRow } from '../../hooks/useBimSeasonsInflationColumns';
import { useBimSeasonsInflationColumns } from '../../hooks/useBimSeasonsInflationColumns';

import { BimContractEditLimitedCosts } from './BimContractEditLimitedCosts';

interface IBimContractEditPriceForm {
    control: Control;
    defaultValues: any;
    seasonsInflationItems?: TBimContractSeasonInflationInputRow[];
    conversionIndicesItems?: TBimContractConversionIndexInput[];
    classNameTooltip?: string;
    fieldsToCheck?: Array<ICollapseFieldsToCheckItem>;
    onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
}

export const bimContractPriceTabStyle = { margin: 'var(--space-margin-bottom)', marginLeft: 0, maxWidth: 1034 };

export const cardStyle = {
    padding: 16,
    background: 'var(--color-bg-stripe)',
    fontWeight: 600,
    display: 'flex ',
    alignItems: 'center',
    fontSize: 14,
    borderRadius: 4,
    boxShadow: 'unset',
};

export const BimContractEditPriceForm: React.FC<IBimContractEditPriceForm> = (props) => {
    const {
        control,
        defaultValues,
        seasonsInflationItems,
        conversionIndicesItems,
        classNameTooltip,
        fieldsToCheck,
        onTooltipAttributeChange,
    } = props;

    const seasonsRange = useWatch({ control, name: 'seasonsRange' });
    const constructionTypes = useWatch({ control, name: 'constructionTypes' });
    const isFormAvailable = useMemo(
        () => validateInterval(seasonsRange) === true && constructionTypes?.length,
        [constructionTypes?.length, seasonsRange],
    );
    const conversionIndicesColumns = useBimConversionIndicesColumns({
        control,
        isEditable: true,
    });
    const seasonColumns = useBimSeasonsInflationColumns({ control, isEditable: true });

    const items = useMemo(
        () => [
            {
                label: 'Индексы перевода в текущий уровень цен',
                content: (
                    <DataTable
                        borderBetweenRows
                        outerBorders
                        columns={conversionIndicesColumns}
                        data={conversionIndicesItems || []}
                    />
                ),
            },
            {
                label: 'Сезон',
                content: (
                    <div style={{ maxWidth: 880 }}>
                        <DataTable
                            borderBetweenRows
                            outerBorders
                            columns={seasonColumns}
                            data={seasonsInflationItems || []}
                        />
                    </div>
                ),
            },
            {
                label: 'Лимитированные затраты',
                content: (
                    <div style={{ maxWidth: 880 }}>
                        <BimContractEditLimitedCosts
                            control={control}
                            defaultValues={defaultValues}
                            classNameTooltip={classNameTooltip}
                            onTooltipAttributeChange={onTooltipAttributeChange}
                        />
                    </div>
                ),
            },
        ],
        [
            classNameTooltip,
            control,
            conversionIndicesColumns,
            conversionIndicesItems,
            defaultValues,
            onTooltipAttributeChange,
            seasonColumns,
            seasonsInflationItems,
        ],
    );

    return (
        <div style={bimContractPriceTabStyle}>
            {!isFormAvailable && (
                <Card style={cardStyle}>
                    Для отображения данных выберите виды строительства и сезоны в разделе &quot;Основная
                    информация&quot;
                </Card>
            )}
            {isFormAvailable && (
                <>
                    <GridItem>
                        <ControlledTextField
                            name="supplyBasis"
                            type="text"
                            placeholder="Базис поставки"
                            label="Базис поставки"
                            labelPosition="left"
                            control={control}
                            required
                            className={cnMixSpace({ mB: '2xl' })}
                            classNameTooltip={classNameTooltip}
                            style={{ width: 675 }}
                            rules={requiredBaseRule}
                            onTooltipAttributeChange={onTooltipAttributeChange}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledCollapseGroup
                            items={items}
                            control={control}
                            fieldsToCheck={fieldsToCheck}
                            classNameTooltip={classNameTooltip}
                            onTooltipAttributeChange={onTooltipAttributeChange}
                            labelWithLessWidth
                        />
                    </GridItem>
                </>
            )}
        </div>
    );
};

BimContractEditPriceForm.displayName = 'BimContractEditPriceForm';
